import React, { useCallback, useEffect, memo } from 'react';
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppDispatch, useAppSelector } from '@/store';
import { Invitation } from '@/types/residents/home';
import { UserType } from '@/types/residents/user';
import { LINKED_ACCOUNTS_COLORS } from '@/constants/residents/mobile/linkedAccountsColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { invitationsStrings } from '@/constants/residents/strings';
import { fetchInvitations, acceptInvitation, declineInvitation, resendInvitation } from '@/store/slices/residents/invitationsSlice';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { useToast } from '@/contexts/admin/ToastContext';
import { getDisplayName } from '@/utils/residents/utils';
import { MobileInvitationCard } from './MobileInvitationCard';
import { MobileEmptyState } from './MobileEmptyState';

const SendInvitationIcon = RESIDENTS_SVG_COMPONENTS.SEND_INVITATION;
const EmptyInboxIcon = RESIDENTS_SVG_COMPONENTS.EMPTY_INBOX;

// Memoized item separator to prevent re-renders
const ItemSeparator = memo(() => <View style={separatorStyle.separator} />);
ItemSeparator.displayName = 'ItemSeparator';

const separatorStyle = StyleSheet.create({
  separator: {
    height: 12,
  },
});

interface MobileInvitationsListProps {
  onSendInvite: () => void;
  onDeclineInvitation: (invitation: Invitation) => void;
  onCancelInvitation: (invitation: Invitation) => void;
}

/**
 * Mobile Invitations List Component
 * Dark theme invitations list for mobile - Matches Figma design
 */
export const MobileInvitationsList: React.FC<MobileInvitationsListProps> = ({
  onSendInvite,
  onDeclineInvitation,
  onCancelInvitation,
}) => {
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const { invitations, isLoading } = useAppSelector((state) => state.invitations);
  const { user } = useAppSelector((state) => state.auth);
  const isResident = user?.type === UserType.RESIDENT;
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  const handleAccept = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(acceptInvitation(invitation.id)).unwrap();
      const displayName = getDisplayName(invitation.user.firstName, invitation.user.lastName) || invitation.user.email;
      showSuccess(`You have successfully accepted an invitation from ${displayName}`);
      dispatch(fetchLinkedAccounts());
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to accept invitation');
    }
  }, [dispatch, showSuccess, showError]);

  const handleDecline = useCallback((invitation: Invitation) => {
    // Open confirmation modal via parent
    onDeclineInvitation(invitation);
  }, [onDeclineInvitation]);

  const handleCancel = useCallback((invitation: Invitation) => {
    // Open confirmation modal via parent
    onCancelInvitation(invitation);
  }, [onCancelInvitation]);

  const handleResend = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(resendInvitation(invitation.id)).unwrap();
      showSuccess(`Invitation resent to ${invitation.user.email}`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to resend invitation');
    }
  }, [dispatch, showSuccess, showError]);

  // Render the contextual action card for sending new invitations
  const renderSendInviteCard = useCallback(() => {
    if (!isResident) return null;
    
    return (
      <TouchableOpacity
        style={styles.contextualActionCard}
        onPress={onSendInvite}
        activeOpacity={0.8}
      >
        <View style={styles.contextualActionIcon}>
          <SendInvitationIcon width={65} height={68} color={LINKED_ACCOUNTS_COLORS.textMuted} />
        </View>
        <View style={styles.contextualActionContent}>
          <View style={styles.contextualActionText}>
            <Text style={styles.contextualActionTitle}>
              {invitationsStrings.sendInvite.title}
            </Text>
            <Text style={styles.contextualActionDescription}>
              {invitationsStrings.sendInvite.description}
            </Text>
          </View>
          <TouchableOpacity 
            style={styles.newInvitationButton} 
            onPress={onSendInvite}
            activeOpacity={0.8}
          >
            <Text style={styles.newInvitationButtonText}>
              {invitationsStrings.sendInvite.cta}
            </Text>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    );
  }, [isResident, onSendInvite]);

  // Render section header
  const renderSectionHeader = useCallback(() => {
    if (invitations.length === 0) return null;
    
    return (
      <Text style={styles.sectionHeader}>{invitationsStrings.list.sectionHeader}</Text>
    );
  }, [invitations.length]);

  // Render header with contextual action and section header
  const renderHeader = useCallback(() => {
    return (
      <View style={styles.headerContainer}>
        {renderSendInviteCard()}
        {renderSectionHeader()}
      </View>
    );
  }, [renderSendInviteCard, renderSectionHeader]);

  // Render invitation item using new card component
  const renderItem = useCallback(({ item }: { item: Invitation }) => {
    return (
      <View style={styles.cardWrapper}>
        <MobileInvitationCard
          invitation={item}
          onAccept={handleAccept}
          onDecline={handleDecline}
          onCancel={handleCancel}
          onResend={handleResend}
        />
      </View>
    );
  }, [handleAccept, handleDecline, handleCancel, handleResend]);

  // Render empty state
  const renderEmpty = useCallback(() => (
    <View style={styles.emptyContainer}>
      <MobileEmptyState
        icon={<EmptyInboxIcon width={72} height={68} color={LINKED_ACCOUNTS_COLORS.textMuted} />}
        title={invitationsStrings.list.emptyTitle}
        description={invitationsStrings.list.emptyDescription}
        iconWidth={72}
        iconHeight={68}
      />
    </View>
  ), []);

  return (
    <FlatList
      data={invitations}
      renderItem={renderItem}
      keyExtractor={(item) => String(item.id)}
      style={styles.list}
      contentContainerStyle={[
        styles.listContent,
        { paddingBottom: 24 + insets.bottom },
        invitations.length === 0 && styles.emptyListContent,
      ]}
      refreshControl={
        <RefreshControl
          refreshing={isLoading}
          onRefresh={handleRefresh}
          tintColor={LINKED_ACCOUNTS_COLORS.accentBlue}
        />
      }
      ListHeaderComponent={renderHeader}
      ListEmptyComponent={renderEmpty}
      showsVerticalScrollIndicator={false}
      ItemSeparatorComponent={ItemSeparator}
      // Performance optimizations
      removeClippedSubviews={true}
      maxToRenderPerBatch={10}
      windowSize={5}
      initialNumToRender={10}
    />
  );
};

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 24,
  },
  emptyListContent: {
    flexGrow: 1,
  },
  headerContainer: {
    gap: 24,
    marginBottom: 12,
  },
  // Contextual Action Card (Send a new invitation)
  contextualActionCard: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  contextualActionIcon: {
    width: 65,
    height: 68,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contextualActionContent: {
    flex: 1,
    gap: 20,
  },
  contextualActionText: {
    gap: 2,
  },
  contextualActionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  contextualActionDescription: {
    fontSize: 14,
    fontWeight: '400',
    color: LINKED_ACCOUNTS_COLORS.textMuted,
    lineHeight: 21,
  },
  newInvitationButton: {
    backgroundColor: LINKED_ACCOUNTS_COLORS.accentPink,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignSelf: 'flex-start',
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
  },
  newInvitationButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: LINKED_ACCOUNTS_COLORS.background, // Dark text on pink button
    lineHeight: 18,
  },
  // Section Header
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: LINKED_ACCOUNTS_COLORS.textPrimary,
    lineHeight: 27,
  },
  // Card wrapper for spacing
  cardWrapper: {
    // Spacing handled by ItemSeparatorComponent
  },
  // Empty State
  emptyContainer: {
    minHeight: 272,
  },
});
